import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const SWIPE_MIN_PX = 48;
const SWIPE_AXIS_LOCK_PX = 10;
const SWIPE_COMMIT_RATIO = 0.22;
const SETTLE_MS = 220;
const DISMISS_MIN_PX = 128;

type UseFingerFollowSwipeOptions = {
  stageRef: RefObject<HTMLElement | null>;
  /** CSS selector for the element that translates with the finger. */
  paneSelector?: string;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Reset drag when the shown item changes. */
  resetKey?: string | number;
  enabled?: boolean;
  /**
   * The pane holds prev/current/next side by side. Then a committed swipe can coast the rest
   * of the way to the neighbour before the index changes; with a single-photo pane the photo
   * would instead fly off into an empty stage, so such panes keep the instant swap.
   */
  filmstrip?: boolean;
  /**
   * Hebrew reading order. The next frame sits on the left, so a finger moving right
   * turns the page forward. Pixel translation follows that side, not the LTR habit.
   */
  rtl?: boolean;
  /** When set, a mostly-vertical swipe moves this node and can dismiss the overlay. */
  dismissRef?: RefObject<HTMLElement | null>;
  onDismiss?: () => void;
};

/**
 * Horizontal finger-follow page turn. Locks vertical scroll while axis is X
 * (non-passive touchmove). Ignores drags that start on a button.
 */
export function useFingerFollowSwipe({
  stageRef,
  paneSelector = '[data-finger-follow-pane]',
  canPrev,
  canNext,
  onPrev,
  onNext,
  resetKey,
  enabled = true,
  filmstrip = false,
  rtl = false,
  dismissRef,
  onDismiss,
}: UseFingerFollowSwipeOptions) {
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const axisLockRef = useRef<'x' | 'y' | null>(null);
  const dragXRef = useRef(0);
  const dragYRef = useRef(0);
  const settleTimerRef = useRef<number | null>(null);
  const canPrevRef = useRef(canPrev);
  const canNextRef = useRef(canNext);
  const onPrevRef = useRef(onPrev);
  const onNextRef = useRef(onNext);
  const onDismissRef = useRef(onDismiss);
  const rtlRef = useRef(rtl);

  canPrevRef.current = canPrev;
  canNextRef.current = canNext;
  onPrevRef.current = onPrev;
  onNextRef.current = onNext;
  onDismissRef.current = onDismiss;
  rtlRef.current = rtl;

  /** Positive when the finger moved toward the next frame. */
  function towardNext(dx: number) {
    return rtlRef.current ? dx : -dx;
  }

  function applyDragX(x: number, settling = false) {
    const stage = stageRef.current;
    if (!stage) return;
    const width = stage.clientWidth || window.innerWidth || 320;
    const clamped = Math.max(-width, Math.min(width, x));
    dragXRef.current = clamped;
    const pane = stage.querySelector<HTMLElement>(paneSelector);
    if (!pane) return;
    pane.style.transition = settling ? `transform ${SETTLE_MS}ms ease-out` : 'none';
    // Always a 3D transform, even at rest: dropping it lets Safari discard the composited
    // layer and re-rasterise the photo, which flashes right after the swipe settles.
    pane.style.transform = `translate3d(${clamped}px, 0, 0)`;
  }

  function applyDragY(y: number, settling = false) {
    const shell = dismissRef?.current;
    if (!shell) return;
    dragYRef.current = y;
    shell.style.transition = settling ? `transform ${SETTLE_MS}ms ease-out` : 'none';
    shell.style.transform = y === 0 ? 'translate3d(0, 0, 0)' : `translate3d(0, ${y}px, 0)`;
  }

  function resetDismissTransform() {
    applyDragY(0, false);
  }

  function rubberBandDx(dx: number) {
    const forward = towardNext(dx);
    if (forward > 0 && !canNextRef.current) return dx * 0.28;
    if (forward < 0 && !canPrevRef.current) return dx * 0.28;
    return dx;
  }

  /** Coast to the neighbour, then hand over to the index change, which re-centres the pane. */
  function settleAndCommit(direction: 1 | -1, commit: () => void) {
    const stage = stageRef.current;
    const width = stage?.clientWidth || window.innerWidth || 320;
    const pane = stage?.querySelector<HTMLElement>(paneSelector) ?? null;
    applyDragX(direction * width, true);

    // Swap content only once the pane has really arrived: a timer that fires a frame early
    // leaves a few pixels to jump, which reads as a flicker over the settled photo.
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      pane?.removeEventListener('transitionend', onTransitionEnd);
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
      commit();
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === pane && event.propertyName === 'transform') finish();
    };
    pane?.addEventListener('transitionend', onTransitionEnd);
    settleTimerRef.current = window.setTimeout(finish, SETTLE_MS + 80);
  }

  // Layout effect, not effect: a passive effect runs after paint, so the new slide would be
  // shown for one frame while the pane still sits at the old offset — the neighbour flashes by.
  useLayoutEffect(() => {
    applyDragX(0, false);
    resetDismissTransform();
    axisLockRef.current = null;
    touchStartRef.current = null;
  }, [resetKey, enabled]);

  useEffect(() => () => {
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = stageRef.current;
    if (!el) return;

    const onStart = (event: TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button, a, video')) {
        touchStartRef.current = null;
        axisLockRef.current = null;
        return;
      }
      // A touch landing mid-coast would yank the pane back to centre.
      if (settleTimerRef.current !== null) return;
      const touch = event.touches[0];
      if (!touch) return;
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      axisLockRef.current = null;
      applyDragX(0, false);
      resetDismissTransform();
    };

    const onMove = (event: TouchEvent) => {
      const start = touchStartRef.current;
      if (!start) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;

      if (!axisLockRef.current) {
        if (Math.abs(dx) < SWIPE_AXIS_LOCK_PX && Math.abs(dy) < SWIPE_AXIS_LOCK_PX) return;
        axisLockRef.current = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      }

      if (axisLockRef.current === 'y') {
        if (!onDismissRef.current || !dismissRef?.current) return;
        event.preventDefault();
        applyDragY(dy, false);
        return;
      }

      event.preventDefault();
      applyDragX(rubberBandDx(dx), false);
    };

    const onEnd = (event: TouchEvent) => {
      const start = touchStartRef.current;
      const axis = axisLockRef.current;
      touchStartRef.current = null;
      axisLockRef.current = null;

      if (!start || axis === 'y') {
        if (axis === 'y' && onDismissRef.current && dismissRef?.current) {
          const touch = event.changedTouches[0];
          const dy = touch ? touch.clientY - start.y : 0;
          if (Math.abs(dy) >= DISMISS_MIN_PX) {
            const offScreen = (dy > 0 ? 1 : -1) * (dismissRef.current.clientHeight || window.innerHeight);
            applyDragY(offScreen, true);
            window.setTimeout(() => onDismissRef.current?.(), SETTLE_MS);
          } else {
            applyDragY(0, true);
          }
        } else if (dragYRef.current !== 0) {
          applyDragY(0, true);
        }
        if (dragXRef.current !== 0) applyDragX(0, true);
        return;
      }

      if (axis !== 'x') {
        if (dragXRef.current !== 0) applyDragX(0, true);
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) {
        applyDragX(0, true);
        return;
      }

      const dx = rubberBandDx(touch.clientX - start.x);
      const width = el.clientWidth || window.innerWidth || 320;
      const commitDx = Math.max(SWIPE_MIN_PX, width * SWIPE_COMMIT_RATIO);

      const forward = towardNext(dx);
      // Positive translation opens the frame on the left, which is next only in Hebrew.
      const openNext: 1 | -1 = rtlRef.current ? 1 : -1;
      if (forward >= commitDx && canNextRef.current) {
        if (filmstrip) settleAndCommit(openNext, () => onNextRef.current());
        else {
          applyDragX(0, false);
          onNextRef.current();
        }
        return;
      }
      if (forward <= -commitDx && canPrevRef.current) {
        if (filmstrip) settleAndCommit((rtlRef.current ? -1 : 1), () => onPrevRef.current());
        else {
          applyDragX(0, false);
          onPrevRef.current();
        }
        return;
      }

      applyDragX(0, true);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, stageRef, paneSelector, filmstrip, dismissRef, onDismiss]);
}
